import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {RouterLink} from '@angular/router';
import {ClientService} from '../../../services/auth/client.service';
import {ClientLoadingService} from '../../../services/auth/client-loading.service';
import {EnrichSettings, HubspotProperty, HubspotReadinessResponse} from '../../../models/hubspot.model';

import {MaterialModule} from '../../../material.module';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {
    EnrichReadiness,
    EnrichReadinessDimensionKey,
    EnrichSetupService,
    isEnrichRuntimeUnavailableError,
} from '../../../services/enrich/enrich-setup.service';
import {EnrichConsoleDiagnosticsService, HubspotAuthService, HubspotService, ReferenceService} from "../../../services/enrich";
import {BreadcrumbsComponent} from '../../../shared/breadcrumbs/breadcrumbs.component';
import {ToastService} from '../../../services/layout/toast.service';
import {
    ConnectConnectionsService,
    type ConnectConnectionPermissions,
    type ConnectServerConnection
} from '../../../services/connect/connect-connections.service';

@Component({
    selector: 'app-enrich-setup',
    templateUrl: './setup.component.html',
    styleUrls: ['./setup.component.scss'],
    host: {class: 'webinars-m3 webinars-shell'},
    standalone: true,
    imports: [MaterialModule, ReactiveFormsModule, RouterLink, BreadcrumbsComponent]
})
export class EnrichSetupComponent implements OnInit, OnDestroy {
    private authService = inject(HubspotAuthService);
    private hubspotService = inject(HubspotService);
    private clientService = inject(ClientService);
    private clientLoadingService = inject(ClientLoadingService);
    private enrichSetupService = inject(EnrichSetupService);
    private referenceService = inject(ReferenceService);
    private toastService = inject(ToastService);
    private enrichDiagnostics = inject(EnrichConsoleDiagnosticsService);
    hubspotConnections: ConnectServerConnection[] = [];

    isLoading: boolean = true;
    error: string | null = null;
    // Setup steps
    currentStep: number = 1;
    totalSteps: number = 6;
    readiness: EnrichReadiness = this.enrichSetupService.getCurrentReadiness();

    // Subscription to the shared readiness observable
    private readinessSubscription: any;

    // Enrich settings object that contains the setupStatus
    enrichSettings: EnrichSettings | null = null;
    private backendReadiness: HubspotReadinessResponse | null = null;

    // Step processing states
    isProcessingAuth: boolean = false;
    isProcessingProperties: boolean = false;
    isProcessingWebhooks: boolean = false;
    isProcessingSettings: boolean = false;

    // HubSpot properties for searchable field lists
    hubspotProperties: HubspotProperty[] = [];
    selectedHubspotConnectionId: string | null = null;
    hubspotConnectionPermissions: ConnectConnectionPermissions | null = null;
    connectionPermissionError: string | null = null;
    isCheckingConnectionPermissions: boolean = false;
    selectedHubspotProfileAllowedPropertyNames: string[] = [];
    propertyScopeError: string | null = null;
    propertyScopeMessage: string | null = null;
    isSavingPropertyScope: boolean = false;
    private connectConnectionsService = inject(ConnectConnectionsService);

    // Auth polling
    private authCheckInterval: any = null;

    // Form for CRM properties
    settingsForm: FormGroup = new FormGroup({
        crmOrgNumberProperty: new FormControl('', Validators.required),
        crmSubUnitNumberProperty: new FormControl('')
    });

    readonly breadcrumbs = [
        {label: 'Shine Enrich', route: '/enrich/setup'},
        {label: 'Setup'},
    ];

    ngOnInit(): void {
        // Subscribe to the shared readiness model used by the guard and sidebar.
        this.readinessSubscription = this.enrichSetupService.readiness$.subscribe(readiness => {
            this.readiness = readiness;
            this.currentStep = readiness.currentStep;
        });

        // Use ClientLoadingService to ensure a client is selected
        this.isLoading = true;
        this.error = null;

        this.clientLoadingService.ensureClientSelected().subscribe({
            next: () => {
                // Client is loaded and selected, proceed with setup status check
                this.checkSetupStatus();
            },
            error: (err) => {
            this.enrichDiagnostics.reportError('enrich.setup.client-selection', err);
                this.error = 'No client selected. Please select a client from the dropdown.';
                this.isLoading = false;
            }
        });
    }

    get hasRequiredConnectionScopes(): boolean {
        if (!this.selectedHubspotConnectionId) return true;
        if (this.isCheckingConnectionPermissions) return false;
        if (this.connectionPermissionError) return false;
        return this.hubspotConnectionPermissions?.hasRequiredScopes === true;
    }

    get readinessDimensions() {
        return this.readiness.dimensions;
    }

    get selectedStepIndex(): number {
        return Math.max(0, Math.min(this.currentStep, this.totalSteps) - 1);
    }

    dimension(key: EnrichReadinessDimensionKey) {
        return this.readiness.dimensions.find(item => item.key === key);
    }

    isDimensionReady(key: EnrichReadinessDimensionKey): boolean {
        return this.dimension(key)?.status === 'ready';
    }

    readinessIcon(key: EnrichReadinessDimensionKey): string {
        const status = this.dimension(key)?.status;
        if (status === 'ready') return 'check_circle';
        if (status === 'unknown') return 'help';
        return 'error';
    }

    readinessStatusLabel(key: EnrichReadinessDimensionKey): string {
        const status = this.dimension(key)?.status;
        if (status === 'ready') return 'Ready';
        if (status === 'unknown') return 'Unknown';
        return 'Action needed';
    }

    private canMutate(): boolean {
        return !this.readiness.runtimeUnavailable;
    }

    private ensureConnectionScopesForAction(actionLabel: string): boolean {
        if (!this.canMutate()) return false;
        if (!this.selectedHubspotConnectionId) {
            this.error = `Select a HubSpot connection before ${actionLabel}.`;
            return false;
        }

        if (this.isCheckingConnectionPermissions) {
            this.error = 'Still checking HubSpot connection permissions. Please wait a few seconds and try again.';
            return false;
        }

        if (!this.hubspotConnectionPermissions) {
            this.error = `Verify the selected HubSpot connection scopes before ${actionLabel}.`;
            return false;
        }

        if (this.hubspotConnectionPermissions?.hasRequiredScopes === false || this.connectionPermissionError) {
            this.error =
                this.connectionPermissionError ||
                `Selected HubSpot connection is missing required scopes. Re-authorize the connection before ${actionLabel}.`;
            return false;
        }

        return true;
    }

    checkSetupStatus(): void {
        this.isLoading = true;
        this.error = null;
        const clientId = this.clientService.client?.id;

        if (!clientId) {
            this.error = 'No client selected';
            this.isLoading = false;
            return;
        }

        this.hubspotService.getSettings(clientId).subscribe(
            (settings: EnrichSettings) => {
                this.loadHubspotProperties(clientId);
                this.loadHubspotConnections();
                this.refreshBackendReadiness(clientId);
                const selectedConnectionId = this.selectedHubspotConnectionId || settings.hubspotConnectionId || null;
                this.applySelectedHubspotConnection(selectedConnectionId);

                this.authService.checkAuthStatusForConnection(clientId, selectedConnectionId).subscribe(
                    authResponse => {
                        if (!settings.setupStatus) {
                            settings.setupStatus = {
                                authenticated: authResponse.authenticated,
                                propertiesCreated: false,
                                webhooksSetup: false,
                                settingsCreated: false,
                                mappingSetup: false,
                                setupComplete: false
                            };

                            if (selectedConnectionId) {
                                settings.hubspotConnectionId = selectedConnectionId;
                            }

                            this.hubspotService.safeUpdateSettings(settings, clientId).subscribe(
                                () => {
                                    this.updateStepStatusesWithBackendRefresh(settings, {authStatus: authResponse});
                                },
                                () => {
                                    this.error = 'Failed to initialize setup status';
                                    this.isLoading = false;
                                }
                            );
                            return;
                        }

                        this.updateStepStatuses(settings, {authStatus: authResponse});
                    },
                    () => {
                        this.updateStepStatuses(settings, {
                            authStatus: {
                                authenticated: false,
                                error: 'Failed to verify HubSpot authentication status.'
                            }
                        });
                    }
                );
            },
            error => {
                if (error.status === 404) {
                    this.authService.checkAuthStatusForConnection(clientId, this.selectedHubspotConnectionId).subscribe(
                        authResponse => this.createDefaultSettings(clientId, authResponse.authenticated),
                        () => this.createDefaultSettings(clientId, false)
                    );
                } else {
                    if (isEnrichRuntimeUnavailableError(error)) {
                        this.readiness = this.enrichSetupService.updateReadinessFromSettings(null, {runtimeUnavailable: true});
                    } else {
                        this.error = 'Failed to load settings';
                    }
                    this.isLoading = false;
                }
            }
        );
    }

    updateStepStatuses(
        settings: EnrichSettings,
        context: { authStatus?: { authenticated: boolean; error?: string } | null } = {},
    ): void {
        // Store the settings object
        this.enrichSettings = settings;
        const preferredConnectionId = this.selectedHubspotConnectionId || settings.hubspotConnectionId || null;
        this.applySelectedHubspotConnection(preferredConnectionId);

        // Initialize form with current settings values
        this.settingsForm.patchValue({
            crmOrgNumberProperty: settings.crmOrgNumberProperty || '',
            crmSubUnitNumberProperty: settings.crmSubUnitNumberProperty || ''
        });

        this.publishCurrentReadiness(context);

        this.isLoading = false;
    }

    createDefaultSettings(clientId: string, isAuthenticated: boolean): void {
        if (!this.canMutate()) return;
        const defaultSettings: EnrichSettings = {
            id: "",
            clientId: clientId,
            subscriptionActive: true,
            syncOnCompanyCreated: true,
            existingCompanySyncFrequency: 'weekly',
            lastUpdateTimestamp: new Date().toISOString(),
            addCompaniesMatchingFilters: false,
            companyCaptureEnabled: false,
            dataProvider: 'brreg',
            dataProviders: ['brreg', 'scb', 'cvr'],
            dataSources: [],
            filters: [],
            mapping: [],
            portalId: 0,
            crmOrgNumberProperty: '',
            crmSubUnitNumberProperty: '',
            batchSize: 100,
            autoRepeat: false,
            repeatIntervalMinutes: 60,
            totalCompanies: 0,
            setupStatus: {
                authenticated: isAuthenticated,
                propertiesCreated: false,
                webhooksSetup: false,
                settingsCreated: false,
                mappingSetup: false,
                setupComplete: false
            }
        };

        this.hubspotService.safeUpdateSettings(defaultSettings, clientId).subscribe(
            () => {
                this.updateStepStatusesWithBackendRefresh(defaultSettings);
            },
            () => {
                this.error = 'Failed to create default settings';
                this.isLoading = false;
            }
        );
    }

    private publishCurrentReadiness(
        context: { authStatus?: { authenticated: boolean; error?: string } | null } = {},
    ): void {
        this.readiness = this.enrichSetupService.updateReadinessFromSettings(this.enrichSettings, {
            selectedConnectionId: this.selectedHubspotConnectionId,
            connectionPermissions: this.hubspotConnectionPermissions,
            connectionPermissionError: this.connectionPermissionError,
            authStatus: context.authStatus,
            backendReadiness: this.backendReadiness,
        });
        this.currentStep = this.readiness.currentStep;
    }

    private refreshBackendReadiness(clientId: string): void {
        this.hubspotService.getReadiness(clientId).subscribe({
            next: (readiness) => {
                this.backendReadiness = readiness;
                if (this.enrichSettings) {
                    this.publishCurrentReadiness();
                }
            },
            error: () => {
                this.backendReadiness = null;
                if (this.enrichSettings) {
                    this.publishCurrentReadiness();
                }
            },
        });
    }

    private updateStepStatusesWithBackendRefresh(
        settings: EnrichSettings,
        context: { authStatus?: { authenticated: boolean; error?: string } | null } = {},
    ): void {
        const clientId = this.clientService.client?.id;
        if (!clientId) {
            this.updateStepStatuses(settings, context);
            return;
        }

        this.hubspotService.getReadiness(clientId).subscribe({
            next: (readiness) => {
                this.backendReadiness = readiness;
                this.updateStepStatuses(settings, context);
            },
            error: () => {
                this.backendReadiness = null;
                this.updateStepStatuses(settings, context);
            },
        });
    }

    // Step 1: Authentication
    async initiateAuth(): Promise<void> {
        if (!this.canMutate()) return;
        const clientId = this.clientService.client?.id;

        if (!clientId) {
            this.error = 'No client selected';
            return;
        }

        // Check if already authenticated before initiating auth
        if (this.isDimensionReady('connection') && this.isDimensionReady('scopes')) {
            console.log('Already authenticated with HubSpot, skipping authentication');
            return;
        }

        // Clear any previous errors or success messages
        this.error = null;
        this.isProcessingAuth = true;

        // Initiate the authentication process
        console.log('Initiating HubSpot authentication...');
        const authWindowOpened = await this.authService.initiateAuth(clientId, this.selectedHubspotConnectionId || undefined);

        if (!authWindowOpened) {
            // If the authentication window failed to open, show an error
            this.error = 'Failed to open authentication window. Please check if popup blockers are disabled and try again.';
            this.isProcessingAuth = false;
            return;
        }

        // If the window opened successfully, start polling for authentication status
        console.log('Authentication window opened, starting polling...');
        this.startAuthStatusPolling();
    }

    // Step 2: Property Creation
    setupProperties(): void {
        if (!this.canMutate()) return;
        const clientId = this.clientService.client?.id;

        if (!clientId) {
            this.error = 'No client selected';
            return;
        }
        if (!this.ensureConnectionScopesForAction('creating HubSpot properties')) return;

        this.isProcessingProperties = true;
        this.error = null;

        this.hubspotService.setup(clientId).subscribe(
            () => {
                // Update setup status
                this.hubspotService.getSettings(clientId).subscribe(
                    (settings: EnrichSettings) => {
                        if (settings.setupStatus) {
                            settings.setupStatus.propertiesCreated = true;

                            this.hubspotService.safeUpdateSettings(settings, clientId).subscribe(
                                () => {
                                    // Update the local enrichSettings object
                                    this.enrichSettings = settings;
                                    this.updateStepStatusesWithBackendRefresh(settings);
                                    this.toastService.showSuccess('HubSpot properties created successfully', 4000);
                                    this.isProcessingProperties = false;
                                },
                                () => {
                                    this.error = 'Failed to update setup status';
                                    this.isProcessingProperties = false;
                                }
                            );
                        }
                    },
                    () => {
                        this.error = 'Failed to load settings';
                        this.isProcessingProperties = false;
                    }
                );
            },
            () => {
                this.error = 'Failed to set up HubSpot properties';
                this.isProcessingProperties = false;
            }
        );
    }

    // Step 3: Webhook Setup (assumed to be automatic)
    setupWebhooks(): void {
        if (!this.canMutate()) return;
        const clientId = this.clientService.client?.id;

        if (!clientId) {
            this.error = 'No client selected';
            return;
        }
        if (!this.ensureConnectionScopesForAction('setting up webhooks')) return;

        this.isProcessingWebhooks = true;
        this.error = null;
        // Since webhook setup is assumed to be automatic, we'll just update the status
        this.hubspotService.getSettings(clientId).subscribe(
            (settings: EnrichSettings) => {
                if (settings.setupStatus) {
                    settings.setupStatus.webhooksSetup = true;

                    this.hubspotService.safeUpdateSettings(settings, clientId).subscribe(
                        () => {
                            // Update the local enrichSettings object
                            this.enrichSettings = settings;
                            this.updateStepStatusesWithBackendRefresh(settings);
                            this.toastService.showSuccess('Webhooks set up successfully', 4000);
                            this.isProcessingWebhooks = false;
                        },
                        () => {
                            this.error = 'Failed to update setup status';
                            this.isProcessingWebhooks = false;
                        }
                    );
                }
            },
            () => {
                this.error = 'Failed to load settings';
                this.isProcessingWebhooks = false;
            }
        );
    }

    // Step 4: Settings Creation
    setupSettings(): void {
        if (!this.canMutate()) return;
        const clientId = this.clientService.client?.id;

        if (!clientId) {
            this.error = 'No client selected';
            return;
        }
        if (!this.ensureConnectionScopesForAction('saving settings')) return;

        // Check if the form is valid
        if (!this.settingsForm.valid) {
            this.error = 'CRM Organization Number is required';
            return;
        }

        this.isProcessingSettings = true;
        this.error = null;
        this.hubspotService.getSettings(clientId).subscribe(
            (settings: EnrichSettings) => {
                if (settings.setupStatus) {
                    // Update settings with form values
                    settings.crmOrgNumberProperty = this.settingsForm.get('crmOrgNumberProperty')?.value || '';
                    settings.crmSubUnitNumberProperty = this.settingsForm.get('crmSubUnitNumberProperty')?.value || '';
                    settings.setupStatus.settingsCreated = true;

                    this.hubspotService.safeUpdateSettings(settings, clientId).subscribe(
                        () => {
                            // Update the local enrichSettings object
                            this.enrichSettings = settings;
                            this.updateStepStatusesWithBackendRefresh(settings);
                            this.toastService.showSuccess('Settings updated successfully', 4000);
                            this.isProcessingSettings = false;
                        },
                        () => {
                            this.error = 'Failed to update setup status';
                            this.isProcessingSettings = false;
                        }
                    );
                }
            },
            () => {
                this.error = 'Failed to load settings';
                this.isProcessingSettings = false;
            }
        );
    }


    // Skip the mapping step
    skipMappingStep(): void {
        if (!this.canMutate()) return;
        const clientId = this.clientService.client?.id;

        if (!clientId) {
            this.error = 'No client selected';
            return;
        }
        if (!this.ensureConnectionScopesForAction('skipping mapping setup')) return;

        this.isProcessingSettings = true;
        this.error = null;
        this.hubspotService.getSettings(clientId).subscribe(
            (settings: EnrichSettings) => {
                if (settings.setupStatus) {
                    settings.setupStatus.mappingSetup = true;

                    this.hubspotService.safeUpdateSettings(settings, clientId).subscribe(
                        () => {
                            // Update the local enrichSettings object
                            this.enrichSettings = settings;
                            this.updateStepStatusesWithBackendRefresh(settings);
                            this.toastService.showSuccess('Field mapping step skipped', 4000);
                            this.isProcessingSettings = false;
                        },
                        () => {
                            this.error = 'Failed to update setup status';
                            this.isProcessingSettings = false;
                        }
                    );
                }
            },
            () => {
                this.error = 'Failed to load settings';
                this.isProcessingSettings = false;
            }
        );
    }

    // Handle selection of the completion step
    onStepperChange(event: any): void {
        if (!this.canMutate()) return;
        // If the step is selected and not already completed, complete the setup

        if (event.selectedIndex == 5 && !this.enrichSettings?.setupStatus?.setupComplete) {
            this.completeSetup();
        }
    }

    // Step 6: Completion
    completeSetup(): void {
        if (!this.canMutate()) return;
        const clientId = this.clientService.client?.id;

        if (!clientId) {
            this.error = 'No client selected';
            return;
        }

        this.hubspotService.getSettings(clientId).subscribe(
            (settings: EnrichSettings) => {
                if (settings.setupStatus
                    && settings.setupStatus.mappingSetup
                    && settings.setupStatus.webhooksSetup
                    && settings.setupStatus.propertiesCreated
                    && settings.setupStatus.settingsCreated
                    && settings.setupStatus.authenticated
                    && !settings.setupStatus.setupComplete
                ) {
                    settings.setupStatus.setupComplete = true;

                    this.hubspotService.safeUpdateSettings(settings, clientId).subscribe(
                        () => {
                            // Update the local enrichSettings object
                            this.enrichSettings = settings;
                            this.updateStepStatusesWithBackendRefresh(settings);
                            this.toastService.showSuccess('Setup completed successfully', 4000);
                        },
                        () => {
                            this.error = 'Failed to update setup status';
                        }
                    );
                }
            },
            () => {
                this.error = 'Failed to load settings';
            }
        );
    }

    loadHubspotConnections(): void {
        this.connectConnectionsService.list().subscribe({
            next: (connections) => {
                const hubspot = (connections || [])
                    .filter((c) => {
                        const providerRef = String(c.provider_ref || c.ref || '').trim().toLowerCase();
                        const type = String((c as any)?.config?.type || '').trim().toLowerCase();
                        if (type && type !== 'oauth2') return false;
                        return providerRef === 'hubspot.oauth' || providerRef === 'hubspot.oauth2' || providerRef.startsWith('hubspot.');
                    })
                    .sort((a, b) => {
                        const aMs = Date.parse(String(a.updated_at || a.created_at || '')) || 0;
                        const bMs = Date.parse(String(b.updated_at || b.created_at || '')) || 0;
                        return bMs - aMs;
                    });

                this.hubspotConnections = hubspot;
                this.backfillHubspotConnectionPermissions(hubspot);

                if (this.selectedHubspotConnectionId) {
                    this.applySelectedHubspotConnection(this.selectedHubspotConnectionId);
                    return;
                }
                if (this.enrichSettings?.hubspotConnectionId) {
                    this.applySelectedHubspotConnection(this.enrichSettings.hubspotConnectionId);
                    return;
                }

                if (hubspot.length === 1) {
                    this.applySelectedHubspotConnection(hubspot[0].id, {persist: true});
                    return;
                }

                const preferredByName = hubspot.find((c) => (c.name || '').trim().toLowerCase() === 'hubspot');
                if (preferredByName) {
                    this.applySelectedHubspotConnection(preferredByName.id, {persist: true});
                    return;
                }

                const connected = hubspot.find((c) => c.connected === true || c.oauth_connected === true);
                if (connected) {
                    this.applySelectedHubspotConnection(connected.id, {persist: true});
                    return;
                }

                this.applySelectedHubspotConnection(null);
            },
            error: (error) => {
                this.enrichDiagnostics.reportError('enrich.setup.load-connections', error);
            },
        });
    }

    // Stop polling for authentication status
    private stopAuthStatusPolling(): void {
        if (this.authCheckInterval) {
            clearInterval(this.authCheckInterval);
            this.authCheckInterval = null;
        }
    }

    /**
     * Loads HubSpot properties for the searchable field lists
     * @param clientId The client ID to load properties for
     */
    loadHubspotProperties(clientId: string): void {
        this.referenceService.getHubspotProperties(clientId).subscribe(
            (properties: HubspotProperty[]) => {
                this.hubspotProperties = properties;
            },
            (error: any) => {
                this.enrichDiagnostics.reportError('enrich.setup.load-hubspot-properties', error);
            }
        );
    }

    private normalizeAllowedPropertyNames(values: unknown): string[] {
        if (!Array.isArray(values)) return [];
        return Array.from(
            new Set(
                values
                    .map((value) => String(value || '').trim())
                    .filter(Boolean),
            ),
        );
    }

    private loadSelectedConnectionProfile(settings?: EnrichSettings | null): void {
        const source = settings || this.enrichSettings;
        const connectionId = String(this.selectedHubspotConnectionId || '').trim();
        if (!source || !connectionId) {
            this.selectedHubspotProfileAllowedPropertyNames = [];
            return;
        }

        const profiles =
            source.hubspotConnectionProfiles && typeof source.hubspotConnectionProfiles === 'object'
                ? source.hubspotConnectionProfiles
                : {};
        const profile = (profiles as Record<string, { allowedPropertyNames?: string[] }>)[connectionId];
        this.selectedHubspotProfileAllowedPropertyNames = this.normalizeAllowedPropertyNames(
            profile?.allowedPropertyNames,
        );
    }

    private applySelectedHubspotConnection(
        connectionId: string | null | undefined,
        options?: { persist?: boolean },
    ): void {
        if (options?.persist && !this.canMutate()) return;
        const selected = String(connectionId || '').trim();
        this.selectedHubspotConnectionId = selected.length ? selected : null;
        this.refreshHubspotConnectionPermissions(this.selectedHubspotConnectionId);
        this.loadSelectedConnectionProfile();
        this.publishCurrentReadiness();

        if (!options?.persist || !this.selectedHubspotConnectionId) return;
        const clientId = this.clientService.client?.id;
        if (!clientId) return;
        this.persistSelectedHubspotConnection(clientId, this.selectedHubspotConnectionId);
    }

    private persistSelectedHubspotConnection(clientId: string, connectionId: string): void {
        if (!this.canMutate()) return;
        const selectedConnectionId = String(connectionId || '').trim();
        if (!selectedConnectionId) return;

        const currentSelected = String(this.enrichSettings?.hubspotConnectionId || '').trim();
        if (currentSelected === selectedConnectionId) return;

        this.hubspotService.getSettings(clientId).subscribe({
            next: (settings) => {
                const persisted = String(settings.hubspotConnectionId || '').trim();
                if (persisted === selectedConnectionId) {
                    this.enrichSettings = settings;
                    this.loadSelectedConnectionProfile(settings);
                    return;
                }

                settings.hubspotConnectionId = selectedConnectionId;
                this.hubspotService.updateSettings(settings, clientId).subscribe({
                    next: (updated) => {
                        this.enrichSettings = updated;
                        this.loadSelectedConnectionProfile(updated);
                        this.updateStepStatusesWithBackendRefresh(updated);
                    },
                    error: (err) => {
                        this.enrichDiagnostics.reportError('enrich.setup.persist-connection', err);
                    },
                });
            },
            error: (err) => {
                this.enrichDiagnostics.reportError('enrich.setup.load-settings-for-connection', err);
            },
        });
    }

    onConnectionProfileAllowlistChange(values: string[] | null | undefined): void {
        this.selectedHubspotProfileAllowedPropertyNames = this.normalizeAllowedPropertyNames(values || []);
        this.propertyScopeError = null;
        this.propertyScopeMessage = null;
    }

    saveConnectionPropertyScope(): void {
        if (!this.canMutate()) return;
        const clientId = this.clientService.client?.id;
        const connectionId = String(this.selectedHubspotConnectionId || '').trim();
        if (!clientId) {
            this.propertyScopeError = 'No client selected';
            return;
        }
        if (!connectionId) {
            this.propertyScopeError = 'Select a HubSpot connection before saving property scope.';
            return;
        }

        this.isSavingPropertyScope = true;
        this.propertyScopeError = null;
        this.propertyScopeMessage = null;

        this.hubspotService.getSettings(clientId).subscribe({
            next: (settings) => {
                const profiles =
                    settings.hubspotConnectionProfiles && typeof settings.hubspotConnectionProfiles === 'object'
                        ? {...settings.hubspotConnectionProfiles}
                        : {};
                const normalizedAllowed = this.normalizeAllowedPropertyNames(
                    this.selectedHubspotProfileAllowedPropertyNames,
                );
                (profiles as Record<string, { allowedPropertyNames?: string[] }>)[connectionId] =
                    normalizedAllowed.length ? {allowedPropertyNames: normalizedAllowed} : {};

                settings.hubspotConnectionProfiles = profiles;
                settings.hubspotConnectionId = connectionId;

                this.hubspotService.updateSettings(settings, clientId).subscribe({
                    next: (updated) => {
                        this.enrichSettings = updated;
                        this.loadSelectedConnectionProfile(updated);
                        this.propertyScopeMessage = normalizedAllowed.length
                            ? `Property scope saved (${normalizedAllowed.length} properties).`
                            : 'Property scope cleared. All mapped properties are allowed.';
                        this.isSavingPropertyScope = false;
                    },
                    error: (error) => {
                        this.enrichDiagnostics.reportError('enrich.setup.save-property-scope', error);
                        this.propertyScopeError = 'Failed to save HubSpot property scope.';
                        this.isSavingPropertyScope = false;
                    },
                });
            },
            error: (error) => {
                this.enrichDiagnostics.reportError('enrich.setup.load-settings-for-property-scope', error);
                this.propertyScopeError = 'Failed to load settings before saving property scope.';
                this.isSavingPropertyScope = false;
            },
        });
    }

    onHubspotConnectionChange(connectionId: string): void {
        if (!this.canMutate()) return;
        this.propertyScopeError = null;
        this.propertyScopeMessage = null;
        this.applySelectedHubspotConnection(connectionId, {persist: true});
    }

    private refreshHubspotConnectionPermissions(connectionId: string | null | undefined): void {
        const selectedId = String(connectionId || '').trim();
        if (!selectedId) {
            this.hubspotConnectionPermissions = null;
            this.connectionPermissionError = null;
            this.isCheckingConnectionPermissions = false;
            return;
        }

        this.isCheckingConnectionPermissions = true;
        this.connectConnectionsService.getPermissions(selectedId, {service: 'shine-enrich'}).subscribe({
            next: (permissions) => {
                this.hubspotConnectionPermissions = permissions;
                const missingScopes = Array.isArray(permissions?.missingScopes)
                    ? permissions.missingScopes.map((scope) => String(scope || '').trim()).filter(Boolean)
                    : [];
                this.connectionPermissionError =
                    permissions?.hasRequiredScopes === false
                        ? missingScopes.length
                            ? `Selected HubSpot connection is missing required scopes for Shine Enrich: ${missingScopes.join(', ')}. Re-authorize this connection.`
                            : 'Selected HubSpot connection is missing required scopes for Shine Enrich. Re-authorize this connection.'
                        : null;
                this.publishCurrentReadiness();
            },
            error: (error) => {
                this.enrichDiagnostics.reportError('enrich.setup.load-connection-permissions', error);
                this.hubspotConnectionPermissions = null;
                this.connectionPermissionError = 'Unable to verify HubSpot connection permissions. Retry or re-authorize the connection.';
                this.isCheckingConnectionPermissions = false;
                this.publishCurrentReadiness();
            },
            complete: () => {
                this.isCheckingConnectionPermissions = false;
                this.publishCurrentReadiness();
            },
        });
    }

    private backfillHubspotConnectionPermissions(connections: ConnectServerConnection[]): void {
        const uniqueIds = Array.from(
            new Set(
                (connections || [])
                    .map((connection) => String(connection?.id || '').trim())
                    .filter(Boolean),
            ),
        );

        for (const connectionId of uniqueIds) {
            this.connectConnectionsService.getPermissions(connectionId, {service: 'shine-enrich'}).subscribe({
                next: () => {},
                error: (error) => {
                    this.enrichDiagnostics.reportError('enrich.setup.backfill-permissions', error);
                },
            });
        }
    }

    // Start polling for authentication status
    private startAuthStatusPolling(): void {
        if (!this.canMutate()) return;
        // Clear any existing interval
        this.stopAuthStatusPolling();

        console.log('Starting HubSpot OAuth status polling...');

        // Start a new polling interval
        this.authCheckInterval = setInterval(() => {
            const clientId = this.clientService.client?.id;
            if (!clientId) {
                this.enrichDiagnostics.reportError('enrich.setup.auth-status-missing-client-id', new Error('clientIdMissing'));
                this.stopAuthStatusPolling();
                this.error = 'Authentication failed: No client selected';
                this.isProcessingAuth = false;
                return;
            }

            this.authService.checkAuthStatusForConnection(clientId, this.selectedHubspotConnectionId).subscribe(
                response => {
                    // If authentication is successful, stop polling and update UI
                    if (response.authenticated) {
                        console.log('HubSpot authentication successful!');
                        this.isProcessingAuth = false;
                        this.loadHubspotProperties(this.clientService.client?.id ?? '');
                        // Update the authentication status in the settings
                        this.hubspotService.getSettings(clientId).subscribe(
                            settings => {

                                if (!settings.setupStatus) {
                                    settings.setupStatus = {
                                        authenticated: true,
                                        propertiesCreated: false,
                                        webhooksSetup: false,
                                        settingsCreated: false,
                                        mappingSetup: false,
                                        setupComplete: false
                                    }
                                }

                                settings.setupStatus.authenticated = true;
                                if (this.selectedHubspotConnectionId) {
                                    settings.hubspotConnectionId = this.selectedHubspotConnectionId;
                                }
                                this.hubspotService.safeUpdateSettings(settings, clientId).subscribe(
                                    () => {
                                        console.log('Authentication status updated in settings during polling');
                                        // Update the local enrichSettings object
                                        this.enrichSettings = settings;
                                        this.updateStepStatusesWithBackendRefresh(settings, {authStatus: response});
                                        this.stopAuthStatusPolling();
                                        this.toastService.showSuccess('Successfully authenticated with HubSpot', 4000);
                                    },
                                    error => {
                                        this.enrichDiagnostics.reportError('enrich.setup.auth-status-update', error);
                                        this.error = 'Failed to update authentication status. Please try again.';
                                        this.stopAuthStatusPolling();
                                        this.isProcessingAuth = false;
                                    }
                                );
                            },
                            error => {
                                this.enrichDiagnostics.reportError('enrich.setup.auth-status-missing-settings', error);
                                this.error = 'Failed to load settings. Please try again.';
                                this.stopAuthStatusPolling();
                                this.isProcessingAuth = false;
                            }
                        );
                    } else if (response.error) {

                        // Check if this is the "No refresh token available" error, which is expected during auth flow
                        if (response.error.includes('No refresh token available')) {
                            console.log('This is an expected error during authentication flow, continuing polling...');
                            // Don't stop polling, don't show error to user, just continue polling
                        } else {
                            // For other errors, stop polling and show error to user
                            this.enrichDiagnostics.reportError('enrich.setup.auth-status-response-error', response.error);
                            this.error = `Authentication failed: ${response.error}`;
                            this.stopAuthStatusPolling();
                            this.isProcessingAuth = false;
                        }
                    }
                },
                error => {
                    this.enrichDiagnostics.reportError('enrich.setup.auth-status-check-failed', error);
                    // Don't stop polling on transient errors, unless max attempts reached
                }
            );
        }, 2000); // Poll every 2 seconds
    }

    /**
     * Filters HubSpot properties based on the search term
     * @param value The search term
     * @returns Filtered list of HubSpot properties
     */
    filterHubspotProperties(value: string | null): HubspotProperty[] {
        if (!value) {
            return this.hubspotProperties;
        }
        const filterValue = value.toLowerCase();
        return this.hubspotProperties.filter(prop =>
            prop.label.toLowerCase().includes(filterValue) ||
            prop.name.toLowerCase().includes(filterValue) ||
            prop.groupName.toLowerCase().includes(filterValue)
        );
    }

    /**
     * Formats the display value for a HubSpot property
     * @param value The property name
     * @returns The formatted display value
     */
    displayHubspotProperty = (value: string): string => {
        if (!value) return '';
        const property = this.hubspotProperties.find(p => p.name === value);
        return property ? property.label : value;
    }

    // Clean up resources when component is destroyed
    ngOnDestroy(): void {
        // Clean up the polling interval when the component is destroyed
        this.stopAuthStatusPolling();

        // Unsubscribe from the readiness observable
        if (this.readinessSubscription) {
            this.readinessSubscription.unsubscribe();
        }
    }
}
